import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { List, X } from "@phosphor-icons/react";

export default function Navbar() {
  const { t, lang, toggle } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const showSolid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { label: t.nav.home, to: "/" },
    { label: t.nav.services, to: "/#services" },
    { label: t.nav.properties, to: "/properties" },
    { label: t.nav.shop, to: "/servizi-online" },
    { label: t.nav.about, to: "/#about" },
    { label: t.nav.news, to: "/news" },
    { label: t.nav.contact, to: "/#contact" },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showSolid
          ? "bg-white/85 backdrop-blur-xl border-b border-[#0A1428]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center"
        >
          {showSolid ? (
            <img
              src="https://customer-assets.emergentagent.com/job_makelaars360-hub/artifacts/5kh8xx10_1.png"
              alt="Makelaars360"
              className="h-14 lg:h-16 w-auto"
            />
          ) : (
            <span className="flex items-baseline gap-2">
              <span className="font-serif text-2xl lg:text-[26px] font-semibold tracking-tight text-white">
                MAKELAARS
              </span>
              <span className="font-serif text-2xl lg:text-[26px] font-semibold text-[#C5A059]">
                360
              </span>
            </span>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors duration-300 uppercase tracking-wider ${
                showSolid
                  ? "text-[#0A1428]/80 hover:text-[#C5A059]"
                  : "text-white/90 hover:text-[#C5A059]"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            data-testid="lang-switcher"
            className={`hidden sm:flex items-center gap-1 text-xs font-semibold uppercase tracking-widest border px-3 py-2 transition-colors ${
              showSolid
                ? "border-[#0A1428]/20 text-[#0A1428] hover:border-[#C5A059] hover:text-[#C5A059]"
                : "border-white/30 text-white hover:border-[#C5A059] hover:text-[#C5A059]"
            }`}
          >
            <span className={lang === "it" ? "text-[#C5A059]" : ""}>IT</span>
            <span className={showSolid ? "text-[#0A1428]/30" : "text-white/40"}>/</span>
            <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
          </button>

          <Link
            to="/#contact"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center bg-[#C5A059] text-[#0A1428] px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#b08d4f] transition-colors"
          >
            {t.nav.cta}
          </Link>

          <button
            className={`lg:hidden ${showSolid ? "text-[#0A1428]" : "text-white"}`}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#0A1428]/10">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-[#0A1428] hover:text-[#C5A059] py-2 uppercase tracking-wider"
              >
                {item.label}
              </Link>
            ))}
            <button
              onClick={toggle}
              data-testid="mobile-lang-switcher"
              className="self-start text-xs font-semibold uppercase tracking-widest border border-[#0A1428]/20 px-3 py-2"
            >
              {lang === "it" ? "IT / EN" : "EN / IT"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
